import React, { useCallback, useEffect, useState } from 'react'
import { FileText, Ship, Image as ImageIcon } from 'lucide-react'
import { projectsApi, SystemWordRaResponse } from '@/lib/projectsApi'
import { SignedImage } from '@/components/SignedImage'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n/config'

const tr = (key: string, options?: Record<string, unknown>) => i18n.t(key, options)

interface Props {
  projectSystemId: string
}

// per-system Word RA view rendered inside the System Detail page's new
// 'word-ra' tab. data comes from GET /api/project-systems/:id/word-ra
// which returns the ra_system_assessment + matching deep-dive +
// diagrams. 404 means this system isn't mapped to a Word RA row →
// EmptyState directs the user to upload at the project level.

export const WordRaSystemView: React.FC<Props> = ({ projectSystemId }) => {
  useTranslation()
  const [data,    setData]    = useState<SystemWordRaResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [error,   setError]   = useState<string | null>(null)
  // bump to re-trigger the fetch effect. used by the visibility-change
  // and mappings-updated listeners below so we don't have to share
  // state with them.
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancel = false
    setLoading(true); setNotFound(false); setError(null)
    // don't blank out `data` while reloading, keep the previous render
    // on screen so the refresh feels invisible when nothing changed.
    projectsApi.getSystemWordRa(projectSystemId)
      .then(d => { if (!cancel) setData(d) })
      .catch((err: any) => {
        if (cancel) return
        // apiFetch throws with .status, fall back to message detection
        if (err?.status === 404 || /not.*found/i.test(err?.message ?? '')) {
          setNotFound(true)
          setData(null)
        } else {
          setError(err?.message ?? tr('wordRa.loadFailed'))
        }
      })
      .finally(() => { if (!cancel) setLoading(false) })
    return () => { cancel = true }
  }, [projectSystemId, reloadKey])

  // refresh triggers:
  //  - visibility change: user switched tabs and came back. cheap re-
  //    fetch covers stale-after-cross-tab-edit.
  //  - same-window event from the Mappings drawer (Phase B follow-up
  //    commit 2 dispatches 'seacuredb:ra-mappings-updated' on save).
  //    no global store needed, the event acts as a one-shot signal.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') setReloadKey(k => k + 1) }
    const onMappingsUpdated = () => setReloadKey(k => k + 1)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('seacuredb:ra-mappings-updated', onMappingsUpdated)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('seacuredb:ra-mappings-updated', onMappingsUpdated)
    }
  }, [])

  if (loading && !data) return <LoadingState />
  if (notFound)         return <EmptyState />
  if (error && !data)   return <ErrorState message={error} />
  if (!data)            return <EmptyState />

  return (
    <div className="space-y-4">
      <DocMetaBar meta={data.ra_document_meta} masterRow={data.ra_system_assessment} />

      {data.ra_excluded_detail
        ? (data.ra_system_assessment.bucket === 'non_target'
            // Section 4.3 (Verification of Non-CBSs): non_cbs_characteristics
            // + Interface / Functional prose + Conclusion. NO grade summary,
            // NO treatment table, NO Acceptance Criteria. architecture is
            // null but diagrams may be present, rendered without an
            // Architecture wrapper.
            ? <NonCbsDeepDive
                detail={data.ra_excluded_detail}
                diagrams={data.diagrams}
                masterRow={data.ra_system_assessment}
              />
            : <DeepDiveContent
                detail={data.ra_excluded_detail}
                diagrams={data.diagrams}
                masterRow={data.ra_system_assessment}
              />)
        : <NonExcludedSummary masterRow={data.ra_system_assessment} />}
    </div>
  )
}

// ─── Sub-views ────────────────────────────────────────────────────────────

const LoadingState: React.FC = () => (
  <div className="flex items-center justify-center py-12 text-sm text-slate-400">{tr('wordRa.loading')}</div>
)

const EmptyState: React.FC = () => (
  <div className="bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-10 text-center">
    <FileText className="h-10 w-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
    <div className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
      {tr('wordRa.emptyTitle')}
    </div>
    <div className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto">
      {tr('wordRa.emptyDescription')}
    </div>
  </div>
)

const ErrorState: React.FC<{ message: string }> = ({ message }) => (
  <div className="text-sm text-rose-500 p-6 text-center">{message}</div>
)

const DocMetaBar: React.FC<{
  meta: SystemWordRaResponse['ra_document_meta']
  masterRow: SystemWordRaResponse['ra_system_assessment']
}> = ({ meta, masterRow }) => (
  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3 flex items-center gap-3 flex-wrap">
    <Ship className="h-4 w-4 text-blue-500 flex-shrink-0" />
    <div className="text-sm font-medium">{masterRow.system_name}</div>
    {masterRow.supplier && (
      <span className="text-xs text-slate-500">· {masterRow.supplier}</span>
    )}
    <div className="ml-auto flex items-center gap-2 flex-wrap">
      {meta.class_society && (
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium">
          {meta.class_society}
        </span>
      )}
      {meta.vessel_name && (
        <span className="text-[10px] text-slate-500">{meta.vessel_name}</span>
      )}
      {meta.revision && (
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
          Rev. {meta.revision}
        </span>
      )}
      <span className="text-[10px] text-slate-400">
        {tr('wordRa.parsed')} {new Date(meta.parsed_at).toLocaleDateString(i18n.resolvedLanguage)}
      </span>
    </div>
  </div>
)

const NonExcludedSummary: React.FC<{ masterRow: SystemWordRaResponse['ra_system_assessment'] }> = ({ masterRow }) => {
  const isPending = masterRow.bucket === 'pending'
  return (
    <Section title={tr('wordRa.riskSummary')}>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
        {isPending
          ? tr('wordRa.pendingDescription')
          : tr('wordRa.bucketDescription', { bucket: labelizeBucket(masterRow.bucket) })}
      </p>
      <MasterRowGrid masterRow={masterRow} />
    </Section>
  )
}

const MasterRowGrid: React.FC<{ masterRow: SystemWordRaResponse['ra_system_assessment'] }> = ({ masterRow }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
    <Fact label={tr('wordRa.shipFunction')} value={masterRow.ship_function} wide />
    <Fact label={tr('wordRa.result')}        value={masterRow.result_label} />
    <Fact label={tr('wordRa.bucket')}        value={labelizeBucket(masterRow.bucket)} />
    <Fact label="E1" value={masterRow.essential_1 ?? '—'} />
    <Fact label="E2" value={masterRow.essential_2 ?? '—'} />
    <Fact label="E3" value={masterRow.essential_3 ?? '—'} />
    <Fact label="E4" value={masterRow.essential_4 ?? '—'} />
    <Fact label="Cat"           value={masterRow.cat ?? '—'} />
    <Fact label="L"  value={masterRow.l_grade != null ? String(masterRow.l_grade) : '—'} />
    <Fact label="P"  value={masterRow.p_grade != null ? String(masterRow.p_grade) : '—'} />
    <Fact label="RL" value={masterRow.rl_value != null ? String(masterRow.rl_value) : '—'} highlight />
  </div>
)

const DeepDiveContent: React.FC<{
  detail:    NonNullable<SystemWordRaResponse['ra_excluded_detail']>
  diagrams:  SystemWordRaResponse['diagrams']
  masterRow: SystemWordRaResponse['ra_system_assessment']
}> = ({ detail, diagrams, masterRow }) => (
  <>
    {/* Identification */}
    {detail.identification && (
      <Section title={tr('wordRa.systemIdentification')}>
        <DefList items={detail.identification} />
      </Section>
    )}

    {/* Acceptance Criteria */}
    {detail.acceptance_criteria && detail.acceptance_criteria.length > 0 && (
      <Section title={tr('wordRa.acceptanceCriteria')}>
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-[9px] text-slate-400 uppercase">
              <th className="py-1.5 pr-2">{tr('wordRa.requirement')}</th>
              <th className="py-1.5 pr-2 w-16">{tr('wordRa.status')}</th>
              <th className="py-1.5">{tr('wordRa.explanation')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {detail.acceptance_criteria.map((row, i) => (
              <tr key={i}>
                <td className="py-1.5 pr-2 align-top text-slate-700 dark:text-slate-200">{row.requirement}</td>
                <td className="py-1.5 pr-2 align-top">
                  <ComplianceChip status={row.compliance_status} />
                </td>
                <td className="py-1.5 align-top text-slate-600 dark:text-slate-400 text-[11px] leading-relaxed">
                  {row.explanation ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    )}

    {/* Characteristics */}
    {detail.characteristics && (
      <Section title={tr('wordRa.systemCharacteristics')}>
        <DefList items={detail.characteristics} />
      </Section>
    )}

    {/* Architecture + diagrams */}
    {(detail.architecture || diagrams.length > 0) && (
      <Section title={tr('wordRa.systemArchitecture')}>
        {detail.architecture?.description && (
          <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
            {detail.architecture.description}
          </p>
        )}

        {diagrams.length > 0 && (
          <div className="space-y-3 mb-3">
            {diagrams.map(d => (
              <DiagramBlock key={d.id} diagram={d} />
            ))}
          </div>
        )}

        {detail.architecture?.hardware && detail.architecture.hardware.length > 0 && (
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-[9px] text-slate-400 uppercase">
                <th className="py-1 pr-2">{tr('fields.hardware')}</th>
                <th className="py-1">{tr('wordRa.specification')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {detail.architecture.hardware.map((hw, i) => (
                <tr key={i}>
                  <td className="py-1 pr-2 align-top text-slate-700 dark:text-slate-200">{hw.name}</td>
                  <td className="py-1 align-top text-slate-600 dark:text-slate-400">{hw.spec}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    )}

    {/* Grade summary, pulled from the detail rows + master fallback */}
    <Section title={tr('wordRa.gradeSummary')}>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-[11px]">
        <GradeChip label="Cat" value={detail.cat_grade ?? masterRow.cat} />
        <GradeChip label="CX"  value={detail.cx_grade} />
        <GradeChip label="CY"  value={detail.cy_grade} />
        <GradeChip label="US"  value={detail.us_grade} />
        <GradeChip label="AT"  value={detail.at_grade} />
        <GradeChip label="H"   value={detail.h_grade} />
        <GradeChip label="AS"  value={detail.as_grade} />
        <GradeChip label="L"   value={fmtN(detail.likelihood_grade) ?? fmtN(masterRow.l_grade)} />
        <GradeChip label="P"   value={fmtN(detail.impact_grade)     ?? fmtN(masterRow.p_grade)} />
        <GradeChip label="RL"  value={fmtN(detail.risk_level)       ?? fmtN(masterRow.rl_value)} highlight />
      </div>

      {/* per-axis impact breakdown: C / I / A / T. source doc carries
          all four separately and the parser stores them, previously
          only the rolled-up P chip surfaced. row hidden when every
          axis is null. */}
      {(detail.impact_c !== null || detail.impact_i !== null
         || detail.impact_a !== null || detail.impact_t !== null) && (
        <div className="mt-2 grid grid-cols-4 gap-2 text-[11px]">
          <ImpactAxisChip label="C" value={detail.impact_c} title={tr('wordRa.confidentiality')} />
          <ImpactAxisChip label="I" value={detail.impact_i} title={tr('wordRa.integrity')} />
          <ImpactAxisChip label="A" value={detail.impact_a} title={tr('wordRa.availability')} />
          <ImpactAxisChip label="T" value={detail.impact_t} title={tr('wordRa.trustSafety')} />
        </div>
      )}
    </Section>

    {/* grade rationale: the per-axis explanations the parser already
        captures for Cat / CX / CY / H. section omitted entirely when
        every explanation is null (no clutter on systems without
        rationale text). */}
    {(detail.cat_explanation || detail.cx_explanation
       || detail.cy_explanation || detail.h_explanation) && (
      <Section title={tr('wordRa.gradeRationale')}>
        <div className="space-y-3">
          <GradeRationale label="Cat" grade={detail.cat_grade ?? masterRow.cat ?? null} prose={detail.cat_explanation} />
          <GradeRationale label="CX"  grade={detail.cx_grade} prose={detail.cx_explanation} />
          <GradeRationale label="CY"  grade={detail.cy_grade} prose={detail.cy_explanation} />
          <GradeRationale label="H"   grade={detail.h_grade}  prose={detail.h_explanation}  />
        </div>
      </Section>
    )}

    {/* treatment: always shown for 4.2 exclusion rows so the author
        can see which fields were left as template placeholders
        (rendered "(not specified)") vs filled in. pre-commit-1 the
        parser stored the verbatim "[Insert ...]" strings. after that
        scrub the slots come through as NULL which DefList now shows
        as italicised "(not specified)" rather than silently dropping. */}
    <Section title={tr('wordRa.riskTreatment')}>
      <DefList
        showNulls
        items={{
          'Treatment decision':       detail.treatment_decision,
          'Existing org. controls':   detail.existing_org_controls,
          'Existing physical controls': detail.existing_physical_controls,
          'Additional measures':      detail.additional_measures,
          'Residual risk statement':  detail.residual_risk_statement,
        }}
      />
    </Section>

    {/* Final statement */}
    {detail.final_statement && (
      <Section title={tr('wordRa.finalStatement')}>
        <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-wrap">
          {detail.final_statement}
        </p>
      </Section>
    )}
  </>
)

// Section 4.3 (Verification of Non-CBSs) display branch. renders the
// non_cbs_characteristics checklist + Interface / Functional prose +
// Conclusion. architecture is null on this path but diagrams may be
// populated, rendered as a plain "Diagrams" section rather than
// inside an Architecture wrapper so `arch.description` is never
// dereferenced and no "(null)" placeholder text leaks through.
const NonCbsDeepDive: React.FC<{
  detail:    NonNullable<SystemWordRaResponse['ra_excluded_detail']>
  diagrams:  SystemWordRaResponse['diagrams']
  masterRow: SystemWordRaResponse['ra_system_assessment']
}> = ({ detail, diagrams }) => (
  <>
    {/* Identification */}
    {detail.identification && (
      <Section title={tr('wordRa.systemIdentification')}>
        <DefList items={detail.identification} />
      </Section>
    )}

    {/* Assessment of CBS Characteristics, 5-item checklist */}
    {detail.non_cbs_characteristics && detail.non_cbs_characteristics.length > 0 && (
      <Section title={tr('wordRa.cbsCharacteristics')}>
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-[9px] text-slate-400 uppercase">
              <th className="py-1.5 pr-2 w-40">{tr('wordRa.item')}</th>
              <th className="py-1.5 pr-2 w-48">{tr('wordRa.assessment')}</th>
              <th className="py-1.5">{tr('wordRa.explanation')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {detail.non_cbs_characteristics.map((row, i) => (
              <tr key={i}>
                <td className="py-1.5 pr-2 align-top text-slate-700 dark:text-slate-200 font-medium">{row.item}</td>
                <td className="py-1.5 pr-2 align-top text-slate-600 dark:text-slate-300">{row.assessment}</td>
                <td className="py-1.5 align-top text-slate-600 dark:text-slate-400 leading-relaxed">
                  {row.explanation ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    )}

    {/* Diagrams (no architecture wrapper for non_target) */}
    {diagrams.length > 0 && (
      <Section title={tr('wordRa.diagrams')}>
        <div className="space-y-3">
          {diagrams.map(d => (
            <DiagramBlock key={d.id} diagram={d} />
          ))}
        </div>
      </Section>
    )}

    {/* Interface Characteristics (prose) */}
    {detail.interface_characteristics && (
      <Section title={tr('wordRa.interfaceCharacteristics')}>
        <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-wrap">
          {detail.interface_characteristics}
        </p>
      </Section>
    )}

    {/* Functional Characteristics (prose) */}
    {detail.functional_characteristics && (
      <Section title={tr('wordRa.functionalCharacteristics')}>
        <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-wrap">
          {detail.functional_characteristics}
        </p>
      </Section>
    )}

    {/* Conclusion (prose), also mirrored into final_statement at
        parse time so the 4.2-facing read path on the same row would
        render this same text. rendering it under "Conclusion" here
        matches the source-doc semantics. */}
    {detail.conclusion_text && (
      <Section title={tr('wordRa.conclusion')}>
        <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-wrap">
          {detail.conclusion_text}
        </p>
      </Section>
    )}
  </>
)

// ─── Helpers ──────────────────────────────────────────────────────────────

const DiagramBlock: React.FC<{ diagram: SystemWordRaResponse['diagrams'][number] }> = ({ diagram }) => {
  const signFn = useCallback(() => projectsApi.signRaDiagram(diagram.id), [diagram.id])
  const title = diagram.kind === 'topology' ? 'Topology Diagram'
              : diagram.kind === 'wiring'   ? 'Wiring Diagram'
              : 'Diagram'
  return (
    <div>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide font-medium text-slate-500 dark:text-slate-400 mb-1.5">
        <ImageIcon className="h-3 w-3" />
        {title}
      </div>
      <SignedImage
        signFn={signFn}
        alt={`${title} for system`}
        className="max-w-full border border-slate-200 dark:border-slate-700 rounded"
      />
    </div>
  )
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
    <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-2">{title}</div>
    {children}
  </div>
)

const DefList: React.FC<{
  items: Record<string, string | null | undefined>
  // when true, null/empty values render as "(not specified)" instead
  // of dropping the row. used by the Treatment block so authors can
  // see which fields the source doc left blank vs which they filled
  // in. defaults to false (existing behaviour: silent drop) so the
  // Identification + Characteristics blocks stay compact.
  showNulls?: boolean
}> = ({ items, showNulls }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-3 gap-y-1 text-[11px]">
    {Object.entries(items).map(([k, v]) => {
      if (!v) {
        if (!showNulls) return null
        return (
          <React.Fragment key={k}>
            <dt className="text-slate-500 dark:text-slate-400">{labelize(k)}</dt>
            <dd className="text-slate-400 dark:text-slate-500 italic">(not specified)</dd>
          </React.Fragment>
        )
      }
      return (
        <React.Fragment key={k}>
          <dt className="text-slate-500 dark:text-slate-400">{labelize(k)}</dt>
          <dd className="text-slate-700 dark:text-slate-200 leading-relaxed">{v}</dd>
        </React.Fragment>
      )
    })}
  </dl>
)

const Fact: React.FC<{ label: string; value: string | null | undefined; highlight?: boolean; wide?: boolean }> = ({ label, value, highlight, wide }) => (
  <div className={`rounded border px-2 py-1.5 ${wide ? 'col-span-2 sm:col-span-2' : ''} ${
    highlight
      ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20'
      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30'
  }`}>
    <div className="text-[9px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="text-[12px] font-medium text-slate-900 dark:text-white">{value ?? '—'}</div>
  </div>
)

const GradeChip: React.FC<{ label: string; value: string | null; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className={`rounded border px-2 py-1.5 text-center ${
    highlight
      ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20'
      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30'
  }`}>
    <div className="text-[9px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="text-sm font-semibold text-slate-900 dark:text-white">{value ?? '—'}</div>
  </div>
)

// per-axis impact chip. same visual weight as GradeChip but renders a
// numeric value and surfaces the full axis name as a title tooltip
// (Confidentiality / Integrity / Availability / Trust-Safety).
const ImpactAxisChip: React.FC<{ label: string; value: number | null; title: string }> = ({ label, value, title }) => (
  <div title={title} className="rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30 px-2 py-1.5 text-center">
    <div className="text-[9px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="text-sm font-semibold text-slate-900 dark:text-white">{value !== null ? `P${value}` : '—'}</div>
  </div>
)

// one row of grade rationale: the per-axis label + grade + the prose
// the parser captured. rendered as a "Cat (III): explanation…" block
// stacked vertically. skipped automatically when prose is null so the
// container doesn't have to filter at the call site.
const GradeRationale: React.FC<{ label: string; grade: string | null; prose: string | null }> = ({ label, grade, prose }) => {
  if (!prose) return null
  return (
    <div className="border-l-2 border-slate-200 dark:border-slate-700 pl-3">
      <div className="text-[10px] uppercase tracking-wide font-medium text-slate-500 dark:text-slate-400 mb-0.5">
        {label}
        {grade && <span className="ml-1.5 text-slate-700 dark:text-slate-200">({grade})</span>}
      </div>
      <p className="text-[11px] text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
        {prose}
      </p>
    </div>
  )
}

const ComplianceChip: React.FC<{ status: string | null }> = ({ status }) => (
  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
    status === 'Y' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : status === 'N' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
    : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
  }`}>{status ?? '—'}</span>
)

// keys whose snake_case → Title Case transcription doesn't match the
// source-doc phrasing the user expects to see. override here. anything
// not in the map falls through to the generic transform.
const LABEL_OVERRIDES: Record<string, string> = {
  interfaces_protocols:   'Interfaces / Protocols',
  update_method:          'Update / Configuration Method',
  external_remote_access: 'External / Remote Access',
  service_ports_media:    'Service Ports / Removable Media',
  os_software_firmware:   'OS / Software / Firmware',
}

function labelize(k: string): string {
  const override = LABEL_OVERRIDES[k]
  if (override) return override
  return k.replace(/_/g, ' ').replace(/\b([a-z])/g, (_, c) => c.toUpperCase())
}

function labelizeBucket(bucket: string): string {
  switch (bucket) {
    case 'target':     return 'Target systems'
    case 'exclusion':  return 'Excluded CBSs'
    case 'non_target': return 'Non-target systems'
    case 'ip_based':   return 'IP-based comm. interfaces'
    case 'pending':    return 'Pending / unassigned'
    default:           return bucket
  }
}

function fmtN(n: number | null | undefined): string | null {
  return n == null ? null : String(n)
}
